import { LineSquiggle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Comment } from './Comment';
import { CommentPill } from './CommentPill';
import type { CommentProps } from './Comment.types';

export interface FileCommentProps extends CommentProps {
  videoTimestampSeconds?: number;
  annotationCount?: number;
}

function formatVideoTimestamp(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function FileComment({
  videoTimestampSeconds,
  annotationCount = 0,
  ...rest
}: FileCommentProps) {
  const { t } = useTranslation('projects');
  const showTimestampPill = videoTimestampSeconds !== undefined;
  const showAnnotationsPill = annotationCount > 0;

  const pills =
    showTimestampPill || showAnnotationsPill ? (
      <>
        {showTimestampPill && (
          <CommentPill
            variant="warning"
            testId="comment-timestamp-pill"
            ariaLabel={t('projectComment.fileComment.annotatedAt', {
              timestamp: formatVideoTimestamp(videoTimestampSeconds),
            })}
            label={formatVideoTimestamp(videoTimestampSeconds)}
          />
        )}
        {showAnnotationsPill && (
          <CommentPill
            className="px-1"
            testId="comment-annotations-pill"
            ariaLabel={t('projectComment.fileComment.annotationCount', {
              count: annotationCount,
            })}
            icon={<LineSquiggle className="size-3.5" />}
          />
        )}
      </>
    ) : undefined;

  return <Comment {...rest} pills={pills} />;
}
